import React, {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { useSelector } from "react-redux";
import AutoSizer from "react-virtualized-auto-sizer";
import { VariableSizeList } from "react-window";
import DOMPurify from "dompurify";
import { marked } from "marked";
import {
  selectHMSMessages,
  selectLocalPeerID,
  selectPeerNameByID,
  selectPermissions,
  useHMSActions,
  useHMSStore,
} from "@100mslive/react-sdk";
import { Box, Flex, Text } from "@100mslive/roomkit-react";
import IconButton from "../../IconButton";
import { ToastManager } from "../Toast/ToastManager";
import { useGetUserData } from "../../hooks/useGetUserData";
import { useSetPinnedMessage } from "../hooks/useSetPinnedMessage";

const MessageMenu = ({ anchorRef, open, onClose, children }) => {
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const menuMinWidth = 140;

  useEffect(() => {
    if (!open) return;
    const a = anchorRef?.current;
    if (!a?.getBoundingClientRect) return;

    const menuMinWidth = 140;

    const computePos = () => {
      const r = a.getBoundingClientRect();
      const viewportLeft = window.scrollX || 0;
      const viewportRight = (window.scrollX || 0) + window.innerWidth;

      const preferRight = (window.scrollX || 0) + r.right + 8;
      let left = preferRight;
      if (preferRight + menuMinWidth > viewportRight) {
        left = viewportLeft + r.left - 8 - menuMinWidth;
      }
      if (left < viewportLeft + 8) left = viewportLeft + 8;

      const top = (window.scrollY || 0) + r.top;
      setPos({ top, left });
    };

    computePos();

    const onDocDown = e => {
      if (!a?.contains?.(e.target)) onClose();
    };
    window.addEventListener("pointerdown", onDocDown);
    window.addEventListener("resize", computePos);
    window.addEventListener("scroll", computePos, true);
    return () => {
      window.removeEventListener("pointerdown", onDocDown);
      window.removeEventListener("resize", computePos);
      window.removeEventListener("scroll", computePos, true);
    };
  }, [open, anchorRef, onClose]);

  if (!open) return null;
  const node = (
    <div
      style={{
        position: "absolute",
        top: pos.top,
        left: pos.left,
        zIndex: 2147483647,
        background: "#374151",
        color: "#fff",
        borderRadius: 8,
        boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
        padding: 2,
        minWidth: menuMinWidth,
      }}
      onPointerDown={e => e.stopPropagation()}
    >
      <div role="menu" style={{ display: "flex", flexDirection: "column" }}>
        {children}
      </div>
    </div>
  );
  return createPortal(node, document.body);
};

const MenuItem = ({ children, onClick }) => {
  const [hover, setHover] = useState(false);
  return (
    <div
      role="menuitem"
      tabIndex={0}
      onClick={e => {
        e.stopPropagation();
        onClick?.();
      }}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: "6px 10px",
        borderRadius: 4,
        marginBottom: 4,
        cursor: "pointer",
        background: hover ? "rgba(255,255,255,0.08)" : "transparent",
        color: "#fff",
        display: "block",
      }}
    >
      {children}
    </div>
  );
};

const formatTime = date => {
  if (!(date instanceof Date)) return "";
  const hours = date.getHours();
  const minutes = date.getMinutes();
  return `${hours < 10 ? "0" : ""}${hours}:${minutes < 10 ? "0" : ""}${minutes}`;
};

export const AnnotisedMessage = ({ message, role, senderName }) => {
  if (!message) return null;

  const isAssistant =
    (role && String(role).toLowerCase() === "assistant") ||
    String(senderName || "").toLowerCase() === "superdoc";

  if (typeof message === "string" && isAssistant) {
    try {
      const raw = marked.parse(String(message || ""));
      const clean = DOMPurify.sanitize(raw, { USE_PROFILES: { html: true } });
      const unwrapped = clean.replace(/^\s*<p[^>]*>([\s\S]*?)<\/p>\s*$/i, "$1");
      const withSafeLinks = unwrapped.replace(
        /<a /g,
        '<a target="_blank" rel="noopener noreferrer" '
      );
      return <span dangerouslySetInnerHTML={{ __html: withSafeLinks }} />;
    } catch (e) {
      return <span>{message}</span>;
    }
  }

  return <span>{String(message)}</span>;
};

const ChatMessage = React.memo(
  ({ index, style = {}, message, setRowHeight, onPin, isPseudoFullscreen }) => {
    const { t } = useTranslation();
    const { ref, inView } = useInView({ threshold: 0.5, triggerOnce: true });
    const rowRef = useRef(null);

    useLayoutEffect(() => {
      if (!rowRef.current) return;
      const key = message.id || `${message.time}-${index}`;
      setRowHeight(key, rowRef.current.clientHeight);
    }, [index, message.message, message.pending, message.failed]);

    useEffect(() => {
      const el = rowRef.current;
      if (
        !el ||
        typeof window === "undefined" ||
        typeof window.ResizeObserver === "undefined"
      )
        return;
      const key = message.id || `${message.time}-${index}`;
      const ro = new window.ResizeObserver(entries => {
        for (const entry of entries) {
          const h = Math.round(
            entry.contentRect.height || el.clientHeight || 0
          );
          setRowHeight(key, h);
        }
      });
      ro.observe(el);
      return () => ro.disconnect();
    }, [message.id, message.time, index, setRowHeight]);

    const hmsActions = useHMSActions();
    const localPeerId = useHMSStore(selectLocalPeerID);
    const localPeerName = useHMSStore(selectPeerNameByID(localPeerId));
    const userData = useGetUserData();
    const normalize = s =>
      typeof s === "string" ? s.trim().toLowerCase() : "";
    const permissions = useHMSStore(selectPermissions);
    const anchorRef = useRef(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const msgNameNorm = normalize(message.senderName);
    const localNameNorm = normalize(localPeerName);
    const userNameNorm = normalize(userData?.name || userData?.displayName);

    const isOwn =
      message.sender === localPeerId ||
      (localNameNorm && msgNameNorm && msgNameNorm === localNameNorm) ||
      (userNameNorm && msgNameNorm && msgNameNorm === userNameNorm);
    const isSuperDocMsg = !!message.__superdoc;

    useEffect(() => {
      if (message.id && !message.read && inView)
        hmsActions?.setMessageRead?.(true, message.id);
    }, [message.read, hmsActions, inView, message.id]);

    const bubbleBg = isOwn ? "$primary_bright" : "$secondary_default";
    const bubbleColor = isOwn ? "$on_primary_high" : "$on_secondary_high";

    return (
      <Box
        ref={ref}
        as="div"
        css={{
          mb: message.__superdoc ? "$4" : isOwn ? "$4" : "$8",
          pr: message.__superdoc ? "$4" : isOwn ? "$4" : "$10",
          display: "block",
          position: "relative",
        }}
        style={style}
      >
        <Flex
          ref={rowRef}
          align="center"
          css={{
            flexWrap: "wrap",
            px: isOwn ? 0 : "$2",
            userSelect: "none",
            justifyContent: isOwn ? "flex-end" : "flex-start",
          }}
          key={message.time}
          data-testid="chat_msg"
        >
          <Text
            css={{
              color: "$on_surface_high",
              fontWeight: "$semiBold",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              flexDirection: isOwn ? "row-reverse" : "row",
            }}
            as="div"
          >
            <Flex align="center">
              {isOwn ? (
                <>
                  <Text
                    as="span"
                    variant="sm"
                    css={{
                      mr: "$4",
                      color: "$on_surface_medium",
                      flexShrink: 0,
                    }}
                  >
                    {formatTime(message.time)}
                  </Text>
                  <Text as="span" css={{ fontWeight: "$semiBold" }}>
                    {message.senderName ||
                      t("lbl_anonymous", { defaultValue: "Anonymous" })}
                  </Text>
                </>
              ) : (
                <>
                  <Text as="span" css={{ fontWeight: "$semiBold" }}>
                    {message.senderName ||
                      t("lbl_anonymous", { defaultValue: "Anonymous" })}
                  </Text>
                  <Text
                    as="span"
                    variant="sm"
                    css={{
                      ml: "$4",
                      color: "$on_surface_medium",
                      flexShrink: 0,
                    }}
                  >
                    {formatTime(message.time)}
                  </Text>
                </>
              )}
            </Flex>

            {!message.__superdoc && permissions.removeOthers && (
              <>
                <IconButton
                  ref={anchorRef}
                  title={t("lbl_more")}
                  aria-label={t("lbl_more")}
                  css={{
                    width: "$12",
                    height: "$12",
                    padding: "$1",
                    fontSize: "18px",
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    setMenuOpen(true);
                  }}
                >
                  ⋯
                </IconButton>
                <MessageMenu
                  anchorRef={anchorRef}
                  open={menuOpen}
                  onClose={() => setMenuOpen(false)}
                >
                  <Box css={{ p: "$4" }}>
                    <MenuItem
                      onClick={() => {
                        setMenuOpen(false);
                        onPin && onPin();
                      }}
                    >
                      {t("lbl_pin_msg")}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        navigator.clipboard
                          ?.writeText?.(String(message.message || ""))
                          .then(() =>
                            ToastManager.addToast({
                              variant: "success",
                              title: t("lbl_copied"),
                            })
                          )
                          .catch(() =>
                            ToastManager.addToast({
                              variant: "error",
                              title: t("lbl_copy_failed"),
                            })
                          );
                        setMenuOpen(false);
                      }}
                    >
                      {t("lbl_copy")}
                    </MenuItem>
                  </Box>
                </MessageMenu>
              </>
            )}
          </Text>

          <Text
            variant="body2"
            css={{
              display: "block",
              w: "auto",
              maxWidth: isPseudoFullscreen ? "50vw" : "72%",
              mt: "$2",
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
              userSelect: "all",
              textAlign: "left",
              background: bubbleBg,
              color: bubbleColor,
              px: "$6",
              py: "$3",
              r: "$2",
              ml: isOwn ? "auto" : 0,
              mr: isOwn ? 0 : "auto",
              position: "relative",
              zIndex: 1,
            }}
            onClick={e => e.stopPropagation()}
          >
            <AnnotisedMessage
              message={message.message}
              senderName={message.senderName}
            />

            {message.failed && (
              <Text css={{ color: "$danger" }} variant="sm">
                {t("lbl_failed_to_send", { defaultValue: "Failed to send" })}
              </Text>
            )}
          </Text>
        </Flex>
      </Box>
    );
  }
);

// Virtualized list and helpers
const ChatList = React.forwardRef(
  ({ width, height, setRowHeight, getRowHeight, messages, isPseudoFullscreen }, listRef) => {
    const { setPinnedMessage } = useSetPinnedMessage();
    const prevCountRef = useRef(messages.length);
    const prevScrollTopRef = useRef(0);
    const mountedRef = useRef(false);

    useEffect(() => {
      const list = listRef.current;
      const outer = list && list._outerRef;
      if (!outer || !list || !list.scrollToItem) {
        prevCountRef.current = messages.length;
        return;
      }

      const distanceFromBottom =
        outer.scrollHeight - (outer.scrollTop + outer.clientHeight);
      const wasNearBottom =
        distanceFromBottom < 100 || prevCountRef.current === 0;

      if (messages.length > prevCountRef.current) {
        if (wasNearBottom) {
          list.scrollToItem(messages.length - 1);
        } else {
          const previousTop = prevScrollTopRef.current || outer.scrollTop;
          requestAnimationFrame(() => {
            outer.scrollTop = previousTop;
          });
        }
      }

      prevCountRef.current = messages.length;
      prevScrollTopRef.current = outer ? outer.scrollTop : 0;
    }, [messages.length, listRef]);

    useEffect(() => {
      const _list = listRef.current;
      if (typeof _list?.resetAfterIndex === "function") {
        if (!mountedRef.current) {
          _list.resetAfterIndex(0, false);
        } else if (messages.length > prevCountRef.current) {
          _list.resetAfterIndex(prevCountRef.current, false);
        }
      }
      mountedRef.current = true;
    }, [messages.length, listRef]);

    return (
      <VariableSizeList
        ref={listRef}
        itemCount={messages.length}
        itemSize={getRowHeight}
        width={width}
        height={height - 1}
        style={{ overflowX: "hidden", overflowY: "auto" }}
      >
        {({ index, style }) => (
          <ChatMessage
            style={style}
            index={index}
            key={`${messages[index].id || "msg"}-${index}`}
            message={messages[index]}
            setRowHeight={setRowHeight}
            onPin={() => setPinnedMessage(messages[index])}
            isPseudoFullscreen={isPseudoFullscreen}
          />
        )}
      </VariableSizeList>
    );
  }
);

const SimpleMessagesList = ({ messages, setPinnedMessage, isPseudoFullscreen }) => {
  const containerRef = useRef();
  const sig = messages
    .map(
      m =>
        `${m.id || "msg"}:${m.time}:${(m.message || "").length}:${m.pending ? 1 : 0}:${m.failed ? 1 : 0}`
    )
    .join("|");

  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    requestAnimationFrame(() => {
      void c.getBoundingClientRect();
      c.scrollTop = c.scrollHeight;
    });
  }, [sig]);

  return (
    <div
      ref={containerRef}
      style={{ overflowY: "auto", height: "100%", padding: 8 }}
    >
      {messages.map((m, i) => (
        <ChatMessage
          key={`${m.id || "msg"}-${i}`}
          index={i}
          message={m}
          setRowHeight={() => {}}
          onPin={() => setPinnedMessage && setPinnedMessage(m)}
          style={{}}
          isPseudoFullscreen={isPseudoFullscreen}
        />
      ))}
    </div>
  );
};

export const VirtualizedChatMessages = React.forwardRef(
  ({ messages, setRowHeight, scrollToBottom, isPseudoFullscreen }, listRef) => {
    const rowHeights = useRef({});

    function getRowHeight(index) {
      const msg = messages && messages[index];
      const key = msg ? msg.id || `${msg.time}-${index}` : index;
      const measured = rowHeights.current[key];
      const base = typeof measured === "number" ? measured : 56;
      return base + 16;
    }

    const setHeight = useCallback(
      (key, size) => {
        rowHeights.current = { ...rowHeights.current, [key]: size };
        const _l = listRef.current;
        if (typeof _l?.resetAfterIndex === "function") {
          const idx = (messages || []).findIndex(
            m => (m.id || `${m.time}-${0}`) === key
          );
          const start = idx === -1 ? 0 : idx;
          requestAnimationFrame(() => {
            const current = listRef.current || _l;
            if (typeof current?.resetAfterIndex === "function") {
              current.resetAfterIndex(start, true);
            }
          });
        }
      },
      [listRef, messages]
    );

    return (
      <Box css={{ h: "100%", width: "100%", overflow: "visible" }} as="div">
        <AutoSizer style={{ width: "100%", height: "100%" }}>
          {({ height, width }) => (
            <ChatList
              width={width}
              height={height}
              messages={messages}
              setRowHeight={setHeight}
              getRowHeight={getRowHeight}
              ref={listRef}
              isPseudoFullscreen={isPseudoFullscreen}
            />
          )}
        </AutoSizer>
      </Box>
    );
  }
);

export const ChatBody = React.forwardRef(
  ({ role, scrollToBottom, showSuperDoc = false }, listRef) => {
    const { t } = useTranslation();
    const storeMessageSelector = role ? selectHMSMessages : selectHMSMessages;
    const messages = useHMSStore(storeMessageSelector) || [];

    const userData = useGetUserData();
    const userKey = `superdoc_conversations:${userData?.id || "anonymous"}`;

    const showingId = useSelector(state => state.counter?.showingId);
    const slideKey = showingId || "global";

    const [storedConvs, setStoredConvs] = useState(() => {
      try {
        const raw =
          typeof window !== "undefined" ? localStorage.getItem(userKey) : null;
        return raw ? JSON.parse(raw) : {};
      } catch (e) {
        console.debug("Failed to parse stored superdoc conversations", e);
        return {};
      }
    });

    useEffect(() => {
      const handler = ev => {
        try {
          const detail = ev && ev.detail ? ev.detail : null;
          const raw =
            typeof window !== "undefined"
              ? localStorage.getItem(userKey)
              : null;
          const existing = raw ? JSON.parse(raw) : {};
          if (detail && detail.userKey && detail.userKey !== userKey) return;
          if (detail && detail.slideKey && detail.slideKey !== slideKey) return;
          setStoredConvs(existing);
        } catch (e) {
          console.debug("superdoc handler parse error", e);
        }
      };
      window.addEventListener("superdoc-conversation-updated", handler);
      return () =>
        window.removeEventListener("superdoc-conversation-updated", handler);
    }, [userKey, slideKey]);

    const convMessages = showSuperDoc
      ? (storedConvs[slideKey] || []).map((c, idx) => ({
          id: `superdoc-${slideKey}-${idx}-${c.time}`,
          senderName: c.sender,
          sender: "",
          time: c.time ? new Date(c.time) : new Date(),
          message: typeof c.message === "string" ? c.message.trim() : c.message,
          pending: !!c.pending,
          failed: !!c.failed,
          retryPayload: c.retryPayload || null,
          recipientRoles: [],
          recipientPeer: null,
          read: true,
          __superdoc: true,
        }))
      : [];

    const mergedMessages = showSuperDoc
      ? [...messages, ...convMessages]
      : messages;

    useEffect(() => {
      if (!showSuperDoc) return;
      requestAnimationFrame(() => {
        try {
          const l = listRef?.current;
          if (!l) return;
          if (typeof l.resetAfterIndex === "function") {
            l.resetAfterIndex(0, true);
          }
          l.scrollToItem(Math.max(0, (mergedMessages || []).length - 1));
        } catch (e) {
          console.debug("ChatBody: scroll to bottom failed", e);
        }
      });
    }, [showSuperDoc, (mergedMessages || []).length]);

    if ((mergedMessages || []).length === 0) {
      return (
        <Flex
          css={{ width: "100%", height: "100%", textAlign: "center", px: "$4" }}
          align="center"
          justify="center"
        >
          <Text>{t("lbl_no_messages_here")}</Text>
        </Flex>
      );
    }

    return (
      <Fragment>
        <VirtualizedChatMessages
          messages={mergedMessages}
          scrollToBottom={scrollToBottom}
          ref={listRef}
        />
      </Fragment>
    );
  }
);

export default ChatBody;
